import { Loader } from "components/common";
import { CUSTOMER_SUCCESS_EMAIL, ENVS, PATH_DASHBOARD, USER_API } from "config";
import { ReactElement, useEffect, useState, useCallback, useRef } from "react";
import { AxiosInstance } from "axios";
import { useAxios } from "hooks/useAxios";
import { useQueryClient } from "@tanstack/react-query";
import { FailedResult } from "../../../components/common/results/FailedResult";
import {
  isOtpPurchaseVerificationReady,
  type OtpCheckoutBaseline,
  type ProjectQuotaApiResponse,
  type ProjectLicenseReadyPayload,
} from "utils/otpPaymentVerification";
import { catalogKeys, sessionKeys } from "queries/profile.keys";
import { fetchSessionUser } from "hooks/useSession/fetchSessionUser";
import { fetchUserCatalog } from "hooks/useSession/fetchUserCatalog";
import { clearOtpCheckoutContext } from "./otpCheckoutContext";
import { OtpPurchaseErrorResult, OtpPurchasePendingResult } from "./results/otp";
import type { SessionUser } from "types/session-user";

type SessionReadyContext = {
  session: SessionUser;
  quotaData?: ProjectQuotaApiResponse | null;
  projectPayload?: ProjectLicenseReadyPayload | null;
};

type ConfirmOneTimeCheckoutResponse = {
  fulfilled: boolean;
  payment_status: string;
  purchase_id: number | null;
  workspace_id: string | null;
};

const confirmOtpCheckoutSession = async (
  axios: AxiosInstance,
  checkoutSessionId: string
): Promise<boolean> => {
  const response = await axios.post(USER_API.confirmOneTimePurchase, {
    checkout_session_id: checkoutSessionId,
  });
  const payload = (response.data.data || response.data) as ConfirmOneTimeCheckoutResponse;
  return Boolean(payload?.fulfilled);
};

export const getUpdatedSessionInfo = async (
  axios: AxiosInstance,
  queryClient: ReturnType<typeof useQueryClient>,
  isSessionReady: (session: SessionUser) => boolean,
  options?: {
    maxTries?: number;
    delayMs?: number;
    workspaceId?: string | null;
    projectId?: string | null;
    pollQuotas?: boolean;
    baselineOtp?: OtpCheckoutBaseline | null;
    checkoutSessionId?: string | null;
    shouldContinue?: () => boolean;
  }
): Promise<{ sessionInfo: SessionUser | null; timedOut: boolean }> => {
  const LONG_POOLING_TRIES = options?.maxTries ?? 30;
  const LONG_POOLING_DELAY_MILLISECONDS = options?.delayMs ?? 1000;
  const workspaceId = options?.workspaceId ?? null;
  const projectId = options?.projectId ?? null;
  const pollQuotas = options?.pollQuotas ?? false;
  const baselineOtp = options?.baselineOtp ?? null;
  const checkoutSessionId = options?.checkoutSessionId?.trim() || null;
  const shouldContinue = options?.shouldContinue ?? (() => true);
  let lastSession: SessionUser | null = null;

  const evaluateReady = (context: SessionReadyContext): boolean => {
    if (pollQuotas) {
      return isOtpPurchaseVerificationReady({
        session: context.session,
        workspaceId,
        quotaData: context.quotaData,
        projectPayload: context.projectPayload,
        baseline: baselineOtp,
      });
    }
    return isSessionReady(context.session);
  };

  for (let i = 0; i < LONG_POOLING_TRIES; i++) {
    if (!shouldContinue()) {
      return { sessionInfo: lastSession, timedOut: false };
    }

    try {
      // Prefer Stripe session confirmation — fulfills immediately if the webhook lagged.
      if (pollQuotas && checkoutSessionId) {
        const fulfilled = await confirmOtpCheckoutSession(axios, checkoutSessionId);
        if (fulfilled) {
          const sessionInfo = await queryClient.fetchQuery({
            queryKey: sessionKeys.me(),
            queryFn: () => fetchSessionUser(axios),
            staleTime: 0,
          });
          lastSession = sessionInfo;
          await queryClient.fetchQuery({
            queryKey: catalogKeys.me(),
            queryFn: () => fetchUserCatalog(axios),
            staleTime: 0,
          });
          return { sessionInfo, timedOut: false };
        }
      }

      const sessionInfo = await queryClient.fetchQuery({
        queryKey: sessionKeys.me(),
        queryFn: () => fetchSessionUser(axios),
        staleTime: 0,
      });
      lastSession = sessionInfo;

      let quotaData: ProjectQuotaApiResponse | null = null;
      let projectPayload: ProjectLicenseReadyPayload | null = null;

      if (pollQuotas) {
        const quotaResponse = await axios.get(USER_API.getProjectQuota);
        quotaData = (quotaResponse.data.data || quotaResponse.data) as ProjectQuotaApiResponse;
      }

      if (projectId) {
        const projectResponse = await axios.get(USER_API.getProject(projectId));
        projectPayload = (projectResponse.data.data ||
          projectResponse.data) as ProjectLicenseReadyPayload;
      }

      if (evaluateReady({ session: sessionInfo, quotaData, projectPayload })) {
        await queryClient.fetchQuery({
          queryKey: catalogKeys.me(),
          queryFn: () => fetchUserCatalog(axios),
          staleTime: 0,
        });
        return { sessionInfo, timedOut: false };
      }
    } catch (error) {
      console.error(error);
    }

    if (!shouldContinue()) {
      return { sessionInfo: lastSession, timedOut: false };
    }

    await new Promise((resolve) => setTimeout(resolve, LONG_POOLING_DELAY_MILLISECONDS));
  }

  return { sessionInfo: lastSession, timedOut: true };
};

interface ProfileInfoLoaderProps {
  isProfileReady?: (session: SessionUser) => boolean;
  children: ReactElement;
  maxTries?: number;
  delayMs?: number;
  timeoutContent?: ReactElement;
  workspaceId?: string | null;
  projectId?: string | null;
  pollQuotas?: boolean;
  baselineOtp?: OtpCheckoutBaseline | null;
  checkoutSessionId?: string | null;
}

export const ProfileInfoLoader: React.FC<ProfileInfoLoaderProps> = ({
  isProfileReady = () => true,
  children,
  maxTries,
  delayMs,
  timeoutContent,
  workspaceId = null,
  projectId = null,
  pollQuotas = false,
  baselineOtp = null,
  checkoutSessionId = null,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  const [sessionInfo, setSessionInfo] = useState<SessionUser | null>(null);
  const isFreePlan = sessionInfo?.pricing_plan_id === Number(ENVS.freePlanId);
  const isOneTimePurchase =
    pollQuotas ||
    sessionInfo?.billing_model === "one_time" ||
    Boolean(sessionInfo?.is_one_time_purchase);

  // Keep full AxiosResponse so quota/project reads can use response.data.data.
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const isProfileReadyRef = useRef(isProfileReady);
  isProfileReadyRef.current = isProfileReady;

  const updateSession = useCallback((session: SessionUser) => {
    setSessionInfo(session);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const getUserInformation = async () => {
      setIsLoading(true);
      setIsError(false);
      setIsTimeout(false);

      try {
        const result = await getUpdatedSessionInfo(
          axios,
          queryClient,
          (session) => isProfileReadyRef.current(session),
          {
            maxTries,
            delayMs,
            workspaceId,
            projectId,
            pollQuotas,
            baselineOtp,
            checkoutSessionId,
            shouldContinue: () => !cancelled,
          }
        );

        if (cancelled) {
          return;
        }

        if (result.sessionInfo) {
          updateSession(result.sessionInfo);
        }

        if (result.timedOut) {
          // Do not soft-succeed: paid Stripe redirect ≠ license granted. Without a verified
          // seat (confirm / webhook / quota progress), show pending so users do not attach yet.
          setIsTimeout(true);
        } else if (!result.sessionInfo) {
          setIsError(true);
        } else if (pollQuotas) {
          clearOtpCheckoutContext();
        }

        setIsLoading(false);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setIsError(true);
          setIsLoading(false);
        }
      }
    };

    void getUserInformation();

    return () => {
      cancelled = true;
    };
  }, [
    axios,
    baselineOtp,
    checkoutSessionId,
    delayMs,
    maxTries,
    pollQuotas,
    projectId,
    queryClient,
    updateSession,
    workspaceId,
  ]);

  if (isLoading) {
    if (pollQuotas || isOneTimePurchase) {
      return <OtpPurchasePendingResult />;
    }
    return <Loader />;
  }

  if (isTimeout) {
    if (timeoutContent) {
      return timeoutContent;
    }
    if (pollQuotas || isOneTimePurchase) {
      return <OtpPurchasePendingResult timedOut />;
    }
    return (
      <FailedResult
        title="We’re finalizing your plan"
        description="We couldn’t verify the plan update yet. This can take a moment. You can retry or pick a plan again."
        actionLink={isFreePlan ? PATH_DASHBOARD.selectPlan : PATH_DASHBOARD.upgrade}
        actionLabel="Choose a Plan"
      />
    );
  }

  if (isError) {
    if (pollQuotas || isOneTimePurchase) {
      return <OtpPurchaseErrorResult />;
    }
    return (
      <FailedResult
        title="Payment Failed"
        description={`Oops! It seems there was an issue processing your payment. Please check your payment details and try again. If the issue persists, contact ${CUSTOMER_SUCCESS_EMAIL}.`}
        actionLink={isFreePlan ? PATH_DASHBOARD.selectPlan : PATH_DASHBOARD.upgrade}
        actionLabel="Try Another Plan"
      />
    );
  }

  return children;
};
